export const EVENT_TABLE_BY_NAME = {
  lead_added: 'lead_events',
  earning_initiated: 'earning_events',
  task_submitted: 'task_events',
};

export function tableForEvent(eventName) {
  return EVENT_TABLE_BY_NAME[eventName] || 'events';
}

// The filter builder's ALL/ANY toggle is stored internally as 'AND'/'OR' (it
// mirrors the connector labels shown between filter rows) — the backend's
// match_type field expects 'ALL'/'ANY' instead.
export function toMatchType(op) {
  return op === 'OR' ? 'ANY' : 'ALL';
}

const PROFILE_FIELD_BY_PROP = {
  Platform: 'mobile_platform',
  City: 'location_city',
  Language: 'language',
  'KYC Status': 'kyc_status',
};

const ENTITY_TABLE_BY_PROP = {
  'Lead Status': 'lead_states',
  'Earning Status': 'earning_states',
  'Task Status': 'task_states',
};

const EARNING_TYPE_BY_PROP = {
  'Project Commission': 'project_commission',
  'Recharge Commission': 'recharge_commission',
  'Referral Bonus': 'referral_bonus',
  'Task Reward': 'task_reward',
};

const CHANNEL_BY_PROP = {
  Push: 'push',
  'In-App': 'inapp',
  SMS: 'sms',
  Email: 'email',
  WhatsApp: 'whatsapp',
  RCS: 'rcs',
  IVR: 'ivr',
};

// filter.timeWindowValue/timeWindowUnit are a free number + unit pair now
// (not a fixed preset) — this just normalizes them into the backend's shape.
function timeWindow(filter) {
  const value = Number(filter.timeWindowValue) || 30;
  const unit = filter.timeWindowUnit || 'days';
  return { value, unit };
}

const PROPERTY_OP_MAP = {
  equals: 'equals',
  'not equals': 'not_equals',
  'greater than': 'greater_than',
  'less than': 'less_than',
  'is set': 'is_set',
  'is not set': 'is_not_set',
};

// Drops incomplete property rows (no name typed yet) rather than sending a
// condition the backend would 400 on.
function propFiltersToProperties(propFilters) {
  if (!propFilters?.length) return undefined;
  const properties = propFilters
    .filter((pf) => pf.name)
    .map((pf) => {
      const operator = PROPERTY_OP_MAP[pf.op] || 'equals';
      if (operator === 'is_set' || operator === 'is_not_set') {
        return { name: pf.name, operator };
      }
      const value = operator === 'greater_than' || operator === 'less_than' ? Number(pf.val) : pf.val;
      return { name: pf.name, operator, value };
    });
  return properties.length ? properties : undefined;
}

// Translates one UI FilterRow object into a backend segment condition. Returns
// null if the filter isn't complete enough to submit yet (e.g. no event chosen).
export function filterToCondition(filter) {
  switch (filter.type) {
    case 'event': {
      if (!filter.event) return null;
      const table = EVENT_TABLE_BY_NAME[filter.event] || 'events';
      const did = filter.did !== false;
      const properties = propFiltersToProperties(filter.propFilters);
      if (!did) {
        return { type: 'event', table, event_name: filter.event, operator: 'has_not_done', time_window: timeWindow(filter), properties };
      }
      const count = Number(filter.countVal) || 1;
      if (count > 1) {
        return { type: 'event', table, event_name: filter.event, operator: 'has_done_more_than', count: count - 1, time_window: timeWindow(filter), properties };
      }
      return { type: 'event', table, event_name: filter.event, operator: 'has_done', time_window: timeWindow(filter), properties };
    }

    case 'user-property': {
      if (!filter.prop || !filter.val) return null;
      return {
        type: 'profile',
        field: PROFILE_FIELD_BY_PROP[filter.prop],
        operator: filter.op === 'not equals' ? 'not_equals' : 'equals',
        value: filter.val,
      };
    }

    case 'entity-status': {
      if (!filter.prop || !filter.val) return null;
      return {
        type: 'entity_state',
        table: ENTITY_TABLE_BY_PROP[filter.prop],
        field: 'current_status',
        operator: filter.op === 'not equals' ? 'not_equals' : 'equals',
        value: filter.val,
      };
    }

    case 'earning': {
      if (!filter.prop || !filter.val) return null;
      return {
        type: 'earning',
        entity_type: EARNING_TYPE_BY_PROP[filter.prop],
        operator: 'sum_greater_than',
        value: Number(filter.val),
        time_window: timeWindow(filter),
      };
    }

    case 'campaign': {
      if (!filter.prop) return null;
      return { type: 'campaign', channel: CHANNEL_BY_PROP[filter.prop], operator: 'not_sent_today' };
    }

    default:
      return null;
  }
}

// Drops incomplete filters rather than sending a condition the backend would 400 on.
export function filtersToConditions(filters) {
  return filters.map(filterToCondition).filter(Boolean);
}

function invert(map) {
  return Object.fromEntries(Object.entries(map).map(([k, v]) => [v, k]));
}

const PROP_BY_PROFILE_FIELD = invert(PROFILE_FIELD_BY_PROP);
const PROP_BY_ENTITY_TABLE = invert(ENTITY_TABLE_BY_PROP);
const PROP_BY_EARNING_TYPE = invert(EARNING_TYPE_BY_PROP);
const PROP_BY_CHANNEL = invert(CHANNEL_BY_PROP);
const OP_LABEL_BY_BACKEND = invert(PROPERTY_OP_MAP);

// Inverse of filterToCondition — loads a saved segment's real backend
// conditions back into editable FilterRow objects. `nextId` supplies unique
// ids the same way SegmentBuilder's own filter-add flow does.
export function conditionsToFilters(conditions, nextId) {
  return conditions.map((condition) => {
    const id = nextId();
    switch (condition.type) {
      case 'event': {
        const did = condition.operator !== 'has_not_done';
        const countVal = condition.operator === 'has_done_more_than' ? String((condition.count || 0) + 1) : '1';
        return {
          id,
          type: 'event',
          event: condition.event_name,
          did,
          countOp: 'at least',
          countVal,
          timeWindowValue: String(condition.time_window?.value || 30),
          timeWindowUnit: condition.time_window?.unit || 'days',
          propFilters: (condition.properties || []).map((p) => ({
            id: nextId(),
            name: p.name,
            op: OP_LABEL_BY_BACKEND[p.operator] || 'equals',
            val: p.value !== undefined ? String(p.value) : '',
          })),
        };
      }

      case 'profile':
        return {
          id,
          type: 'user-property',
          prop: PROP_BY_PROFILE_FIELD[condition.field] || '',
          op: condition.operator === 'not_equals' ? 'not equals' : 'equals',
          val: condition.value,
        };

      case 'entity_state':
        return {
          id,
          type: 'entity-status',
          prop: PROP_BY_ENTITY_TABLE[condition.table] || '',
          op: condition.operator === 'not_equals' ? 'not equals' : 'equals',
          val: condition.value,
        };

      case 'earning':
        return {
          id,
          type: 'earning',
          prop: PROP_BY_EARNING_TYPE[condition.entity_type] || '',
          op: '≥',
          val: String(condition.value),
          timeWindowValue: String(condition.time_window?.value || 30),
          timeWindowUnit: condition.time_window?.unit || 'days',
        };

      case 'campaign':
        return {
          id,
          type: 'campaign',
          prop: PROP_BY_CHANNEL[condition.channel] || '',
          op: 'not sent today',
        };

      default:
        return null;
    }
  }).filter(Boolean);
}
